// ─────────────────────────────────────────────────────────────────
// Glass-i — Process Master helpers (Stage 6B.8A-3K).
//
// Pure functions that the spreadsheet UI uses for state management.
// Importing these into a test file works without React.
// ─────────────────────────────────────────────────────────────────

// Map a backend row (with `thicknessRates: []`) into the
// thickness-keyed shape the grid actually edits:
//
//   { ...row, thicknessRateMap: { 5: 35, 12: 45 } }
//
export function normalizeProcessRows(rows = []) {
  return (rows || []).map((row) => {
    const map = {};
    for (const r of (row.thicknessRates || [])) {
      if (r && r.thicknessMm != null && r.rate != null) {
        map[Number(r.thicknessMm)] = Number(r.rate);
      }
    }
    return { ...row, thicknessRateMap: map };
  });
}

// Inverse: turn the operator's edited thicknessRateMap back into the
// `[{thicknessMm, rate}]` array the backend expects.
export function flattenThicknessRates(thicknessRateMap = {}) {
  return Object.entries(thicknessRateMap)
    .filter(([, rate]) => rate !== '' && rate !== null && rate !== undefined)
    .map(([thicknessMm, rate]) => ({
      thicknessMm: Number(thicknessMm),
      rate:        Number(rate)
    }));
}

// Build the {rows:[…]} body for POST /processes/bulk-save.
// `visibleThicknesses` tells the backend which thicknesses the
// operator can SEE — any of those without an entry in `thicknessRates`
// means the cell was cleared on purpose → backend deletes it.
//
// Stage 6B.8A-3K.5 — when a row has a `departmentAssignments` array,
// it is normalised + included in the payload so the backend replaces
// the live assignment set transactionally.
export function buildBulkSavePayload(rows, visibleThicknesses) {
  const visible = sortThicknesses(visibleThicknesses);
  return {
    rows: (rows || []).map((row) => {
      const out = {
        processName:         (row.processName || '').trim(),
        abbreviation:        (row.abbreviation || '').trim() || null,
        uomCode:             row.uomCode || 'PCS',
        defaultRate:         Number(row.defaultRate || 0),
        processGroup:        row.processGroup
                              || processGroupFromDepartment(row.departmentCode),
        departmentCode:      row.departmentCode || null,
        appliesToCategories: Array.isArray(row.appliesToCategories)
                              ? row.appliesToCategories : [],
        isActive:            row.isActive !== false,
        remarks:             row.remarks || null,
        thicknessRates:      flattenThicknessRates(row.thicknessRateMap),
        visibleThicknesses:  visible,
        _clientRowId:        row._clientRowId || row.id || null
      };
      if (row.id) out.id = row.id;
      if (row.processCode) out.processCode = row.processCode;
      if (Array.isArray(row.departmentAssignments)) {
        out.departmentAssignments =
          buildDepartmentAssignmentPayload(row.departmentAssignments);
      }
      return out;
    })
  };
}

// Sort + dedupe a thickness list ascending.
export function sortThicknesses(list = []) {
  const set = new Set();
  for (const t of (list || [])) {
    const n = Number(t);
    if (Number.isFinite(n) && n > 0) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

// Add a thickness column. Returns { thicknesses, error } so the
// caller can warn on duplicate / invalid input.
export function addThicknessColumn(current, raw) {
  const n = Number(String(raw || '').trim());
  if (!Number.isFinite(n) || n <= 0) {
    return { thicknesses: current, error: 'Enter a positive number.' };
  }
  if (current.includes(n)) {
    return { thicknesses: current, error: `Thickness ${n}mm already exists.` };
  }
  return { thicknesses: sortThicknesses([...current, n]), error: null };
}

// Remove a thickness column. SAFE: this only hides the column from
// the UI; DB rates are kept until the operator clears them and saves.
export function removeThicknessColumn(current, n) {
  return current.filter((t) => Number(t) !== Number(n));
}

// UI-level validation. Returns { ok, errors:[{row, message}] }.
export function validateProcessRows(rows = []) {
  const errors = [];
  if (!Array.isArray(rows) || rows.length === 0) {
    errors.push({ row: -1, message: 'Enter at least one process before saving.' });
    return { ok: false, errors };
  }
  rows.forEach((row, idx) => {
    if (!row.processName || !String(row.processName).trim()) {
      errors.push({ row: idx, message: 'Process name is required.' });
    }
    if (!row.uomCode) {
      errors.push({ row: idx, message: 'UOM is required.' });
    }
    if (Number(row.defaultRate || 0) < 0) {
      errors.push({ row: idx, message: 'Default rate cannot be negative.' });
    }
    if (row.thicknessRateMap) {
      for (const [t, rate] of Object.entries(row.thicknessRateMap)) {
        if (rate === '' || rate === null || rate === undefined) continue;
        if (Number(rate) < 0) {
          errors.push({ row: idx,
            message: `Thickness rate (${t}mm) cannot be negative.` });
        }
      }
    }
  });
  return { ok: errors.length === 0, errors };
}

// Dept ⇄ process_group helpers — keep the spreadsheet column simple.
const DEPT_TO_GROUP = {
  POLISH:       'polish',
  DESIGN:       'design',
  PAINT:        'paint',
  CUTTING:      'cutting',
  INSTALLATION: 'installation',
  OTHERS:       'other'
};
const GROUP_TO_DEPT = {
  polish:       'POLISH',
  design:       'DESIGN',
  paint:        'PAINT',
  cutting:      'CUTTING',
  installation: 'INSTALLATION',
  other:        'OTHERS',
  cutout:       'OTHERS',
  fabrication:  'OTHERS',
  lamination:   'OTHERS',
  dgu:          'OTHERS',
  toughening:   'OTHERS'
};

export function processGroupFromDepartment(dept) {
  return DEPT_TO_GROUP[String(dept || '').toUpperCase()] || 'fabrication';
}

export function departmentFromProcessGroup(group) {
  return GROUP_TO_DEPT[String(group || '').toLowerCase()] || 'OTHERS';
}

// `dirty` is a Set of clientRowIds — a row is dirty if it (a) is new
// (no id) OR (b) the operator edited a field on it.
export function isDirtyRow(row, dirtySet) {
  if (!row) return false;
  if (!row.id) return true;
  return dirtySet && dirtySet.has(row._clientRowId || row.id);
}

export function formatRate(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

// Tiny client-side id generator for new rows (so dirty tracking
// works before the backend assigns a UUID).
let _seq = 0;
export function nextClientRowId() {
  _seq += 1;
  return `new-${Date.now().toString(36)}-${_seq}`;
}

// ── Stage 6B.8A-3K.2 — UI polish helpers ────────────────────────
// Centralised column widths so the spreadsheet can fit large rate
// values like 12200.34 without truncation. Right-aligned numeric
// columns need ≥ 96px to display "12200.34" comfortably; defaultRate
// gets a touch more headroom because it's the column most operators
// scan first. Frozen so a stale import never silently changes width.
export const COLUMN_WIDTHS = Object.freeze({
  // Stage 6B.8A-3K.4 + 3K.6 — widths tuned to the spreadsheet reference.
  // thicknessRate sized for the operator-accepted xxxx.xx envelope
  // ("9999.99" = ~50px text + 16px cell padding ≈ 72px). Six-digit
  // values will wrap, which is acceptable for the rare row that
  // carries them. department widened so the multi-select tag renders
  // the full "Machine Polishing" name. actions trimmed to the test
  // floor (96) since the column carries three borderless icons.
  sno:           38,
  processName:   240,
  abbreviation:  88,
  uom:           82,
  defaultRate:   80,
  department:    150,
  categories:    150,
  thicknessRate: 72,
  actions:       96
});

// Display a rate with 2 decimals. Empty / non-numeric returns ''.
// Lets cell placeholders show "0.00" / "120.45" / "12200.34" the
// same way the InputNumber will after blur (precision={2}).
export function rateDisplay(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

// Category select-all behaviour.
//
// Semantic contract:
//   • Empty array       → "applies to all categories" (placeholder shown)
//   • Non-empty array   → applies only to the listed categories
//
// "Select All" populates the explicit list (so the operator can see
// every category as a chip and de-select individually). "Clear" goes
// back to the implicit-all empty array. We deliberately do NOT
// auto-normalise "all selected → []" because the operator's intent is
// recorded by which button they clicked.
export function selectAllCategories(allCategoryValues) {
  return Array.isArray(allCategoryValues) ? [...allCategoryValues] : [];
}
export function clearCategories() {
  return [];
}
// True when the array semantically means "applies to all categories".
export function isAllCategories(arr) {
  return !Array.isArray(arr) || arr.length === 0;
}

// ── Stage 6B.8A-3K.5 — Department-assignment helpers ────────────
//
// Each helper is pure (no React, no DOM). The frontend node:test
// runner exercises them in `__tests__/processMasterHelpers.test.js`.
//
// Assignment shape (camelCase, frontend-side):
//   {
//     id?:           string  — server-assigned UUID once persisted
//     departmentId:  string  — UUID of the production_departments row
//     departmentCode?: string — joined display field (read-only)
//     departmentName?: string — joined display field (read-only)
//     workMode?:     string  — joined display field (read-only)
//     isDefault:     boolean
//     pricingMode:   'same_as_process' | 'department_override'
//     rateOverride:  number | null     — required when pricingMode = override
//     notes?:        string | null
//     isActive:      boolean
//   }

export const PRICING_MODES = Object.freeze([
  { value: 'same_as_process',     label: 'Same as Process' },
  { value: 'department_override', label: 'Department Override' }
]);

// Normalise a raw API row (camelCase from the backend) into the
// frontend's working shape. Defensive against missing fields so the
// grid never explodes on a partial response.
export function normalizeDepartmentAssignments(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    id:             r.id || null,
    departmentId:   r.departmentId || null,
    departmentCode: r.departmentCode || null,
    departmentName: r.departmentName || null,
    workMode:       r.workMode       || null,
    departmentType: r.departmentType || null,
    isDefault:      r.isDefault === true,
    pricingMode:    r.pricingMode === 'department_override'
                      ? 'department_override' : 'same_as_process',
    rateOverride:   r.rateOverride == null || r.rateOverride === ''
                      ? null : Number(r.rateOverride),
    notes:          r.notes || null,
    isActive:       r.isActive !== false
  }));
}

// Build the payload sent to the backend. Strips client-only fields
// (display joins) and forces `rateOverride = null` for same_as_process
// rows — matches the backend validator's last-mile guard so the API
// can never reject a same-mode row that carried a leftover override.
export function buildDepartmentAssignmentPayload(assignments) {
  if (!Array.isArray(assignments)) return [];
  return assignments
    .filter((a) => a && a.departmentId)
    .map((a) => {
      const mode = a.pricingMode === 'department_override'
        ? 'department_override' : 'same_as_process';
      const out = {
        departmentId: a.departmentId,
        isDefault:    a.isDefault === true,
        pricingMode:  mode,
        rateOverride: mode === 'department_override'
                        ? (a.rateOverride == null || a.rateOverride === ''
                            ? null : Number(a.rateOverride))
                        : null,
        notes:        a.notes || null,
        isActive:     a.isActive !== false
      };
      if (a.id) out.id = a.id;
      return out;
    });
}

// Short text summary suitable for inline display when the cell is
// not in edit mode. e.g. "Manual Polishing, Machine Polishing"
// for ≤3 deps; otherwise "Manual Polishing +2 more".
export function summarizeDepartmentAssignments(assignments) {
  const live = (assignments || []).filter((a) => a && a.isActive !== false);
  if (live.length === 0) return '';
  const names = live.map((a) => a.departmentName || a.departmentCode || '?');
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

// Form-level last-mile guard mirroring the backend Joi rules. Returns
// `{ ok: boolean, errors: [{ field, message }] }`. The drawer should
// call this before submitting so the operator gets inline feedback
// without a round trip.
export function validateDepartmentAssignments(assignments) {
  const errors = [];
  if (!Array.isArray(assignments) || assignments.length === 0) {
    return { ok: true, errors };
  }
  const seenIds = new Set();
  let defaultCount = 0;
  for (let i = 0; i < assignments.length; i += 1) {
    const a = assignments[i] || {};
    if (!a.departmentId) {
      errors.push({ field: `assignments[${i}].departmentId`,
        message: 'Department is required.' });
      continue;
    }
    if (seenIds.has(a.departmentId)) {
      errors.push({ field: `assignments[${i}].departmentId`,
        message: 'Duplicate department assignment is not allowed.' });
    }
    seenIds.add(a.departmentId);
    if (a.pricingMode === 'department_override') {
      if (a.rateOverride == null || a.rateOverride === ''
       || Number(a.rateOverride) < 0) {
        errors.push({ field: `assignments[${i}].rateOverride`,
          message: 'Rate override is required when pricing mode is Department Override.' });
      }
    }
    if (a.isDefault && a.isActive !== false) {
      defaultCount += 1;
      if (defaultCount > 1) {
        errors.push({ field: `assignments[${i}].isDefault`,
          message: 'Only one default department is allowed.' });
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

// Seed shape for a brand-new assignment row in the drawer.
export function defaultDepartmentAssignment(departmentId, departmentName) {
  return {
    id:             null,
    departmentId:   departmentId || null,
    departmentName: departmentName || null,
    isDefault:      false,
    pricingMode:    'same_as_process',
    rateOverride:   null,
    notes:          null,
    isActive:       true
  };
}

// One-liner used in the Dept cell of the grid when not editing.
// Returns "Manual Polishing" for the default (or first) assignment,
// or a plain label when there are none.
export function resolveDepartmentDisplay(assignments) {
  const live = (assignments || []).filter((a) => a && a.isActive !== false);
  if (live.length === 0) return '';
  const def = live.find((a) => a.isDefault) || live[0];
  const name = def.departmentName || def.departmentCode || '';
  if (live.length === 1) return name;
  return `${name} +${live.length - 1}`;
}

// Group a flat department-lookup list into the AntD `OptGroup` shape:
//   [{ label: 'Cutting', options: [{ value, label, workMode }] }, …]
// Used by the multi-select dropdown so operators see Manual / Machine
// nested under the main department (Cutting / Polishing / …).
//
// Input rows must carry: id, departmentName, departmentCode,
// parentDepartmentId (nullable), workMode (nullable).
//
// Strategy:
//   • Main departments (parentDepartmentId === null) become groups.
//   • Sub rows are pushed under their parent group.
//   • Orphan subs (parent missing in input) bubble up to a synthetic
//     "Other" group so nothing silently disappears.
//   • Childless mains become a single-option group containing themselves.
export function groupDepartmentOptions(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  // Stage 6B.8A-3K.6 — sort by execution_order (NULLS LAST), then
  // sequenceNo, then code, so the picker mirrors the production
  // flow (Cutting before Polish before Fabrication …). The backend
  // already returns lookups in this order; we re-sort defensively
  // so callers that hand in unsorted data still get a sensible UI.
  const sortByFlow = (a, b) => {
    const ao = a && a.executionOrder != null
                ? Number(a.executionOrder) : Number.POSITIVE_INFINITY;
    const bo = b && b.executionOrder != null
                ? Number(b.executionOrder) : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    const as = Number((a && a.sequenceNo) || 0);
    const bs = Number((b && b.sequenceNo) || 0);
    if (as !== bs) return as - bs;
    return String((a && a.departmentCode) || '')
      .localeCompare(String((b && b.departmentCode) || ''));
  };
  const ordered = [...rows].sort(sortByFlow);

  const mainsById = new Map();
  const subsByParent = new Map();
  const orphans = [];
  for (const r of ordered) {
    if (!r || !r.id) continue;
    if (!r.parentDepartmentId) {
      mainsById.set(r.id, r);
      if (!subsByParent.has(r.id)) subsByParent.set(r.id, []);
    }
  }
  for (const r of ordered) {
    if (!r || !r.id || !r.parentDepartmentId) continue;
    if (mainsById.has(r.parentDepartmentId)) {
      subsByParent.get(r.parentDepartmentId).push(r);
    } else {
      orphans.push(r);
    }
  }
  // Children within a main are already in flow order from `ordered`.
  const optionFor = (r) => ({
    value:    r.id,
    label:    r.departmentName || r.departmentCode || '',
    code:     r.departmentCode || '',
    workMode: r.workMode || null
  });
  const groups = [];
  for (const main of mainsById.values()) {
    const subs = subsByParent.get(main.id) || [];
    if (subs.length === 0) {
      // Childless main → render as its own picker option (no nesting).
      groups.push({
        label:   main.departmentName || main.departmentCode || '',
        options: [optionFor(main)]
      });
    } else {
      groups.push({
        label:   main.departmentName || main.departmentCode || '',
        options: subs.map(optionFor)
      });
    }
  }
  if (orphans.length > 0) {
    groups.push({ label: 'Other', options: orphans.map(optionFor) });
  }
  return groups;
}
